'use client';

import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import debounce from 'lodash/debounce';
import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@src/utils/tailwind';
const DEBOUNCE_DELAY = 500;
interface SearchInputProps {
  initialValue?: string;
  /** Called (debounced) as the user types. */
  onChange?: (value: string) => void;
  onSubmit?: (value: string) => void;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
  isDarkMode?: boolean;
  dataTestId?: string;
}
export function SearchInput({
  initialValue,
  onChange,
  onSubmit,
  placeholder,
  className,
  autoFocus,
  dataTestId,
  isDarkMode = false
}: SearchInputProps) {
  const [inputValue, setInputValue] = useState(initialValue ?? '');

  // Keep the latest onChange without re-creating the debounced function each render.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const debouncedChange = useMemo(() => debounce((value: string) => onChangeRef.current?.(value), DEBOUNCE_DELAY), []);
  useEffect(() => () => debouncedChange.cancel(), [debouncedChange]);

  // Update the input value when running a new search by using the modal again ("/search" page scenario).
  useEffect(() => {
    if (initialValue !== undefined) {
      setInputValue(initialValue);
    }
  }, [initialValue]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value_0 = e.target.value;
    setInputValue(value_0);
    debouncedChange(value_0.trim());
  };
  const handleClear = () => {
    debouncedChange.cancel();
    setInputValue('');
    onChangeRef.current?.('');
  };
  const handleSubmit = (e_0: React.FormEvent) => {
    e_0.preventDefault();
    debouncedChange.cancel();
    onSubmit?.(inputValue.trim());
  };
  const hasQuery = !!inputValue.trim();
  return <form onSubmit={handleSubmit} className="w-full md:w-auto">
      <div className={cn('relative flex items-center', className ?? '')}>
        <MagnifyingGlassIcon className={cn('absolute left-3 size-5', {
        'text-bodi-gray-butters': isDarkMode,
        'text-bodi-gray-medium': !isDarkMode
      })} />
        <input autoFocus={autoFocus} type="text" name="query" autoComplete="off" value={inputValue} onChange={handleInputChange} placeholder={placeholder} data-testid={dataTestId} className={cn('w-full rounded-lg border py-4 pr-4 pl-10 text-body-base focus:ring-0 focus:outline-none', {
        'pr-10': hasQuery,
        'border-bodi-gray-medium bg-bodi-black-base text-bodi-white placeholder:text-bodi-white focus:border-bodi-white': isDarkMode,
        'border-bodi-gray-pearl bg-bodi-gray-pearl text-bodi-black placeholder:bg-bodi-gray-pearl placeholder:text-bodi-black-text focus:border-bodi-black': !isDarkMode
      })} />
        {hasQuery && <button type="button" onClick={handleClear} aria-label="Clear search" className={cn('absolute right-3', {
        'text-bodi-gray-butters hover:text-bodi-white': isDarkMode,
        'text-bodi-gray-disabled hover:text-bodi-black': !isDarkMode
      })}>
            <XMarkIcon className="size-5" />
          </button>}
      </div>
    </form>;
}